import React, { useEffect, useState } from "react";

// ================= AXIOS + API =================
import axios from "axios";
import { API_BASE } from "../apiConfig";

// ================= CHARTS =================
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);


// ======================================
// CATÁLOGO OFICIAL (11 DISPOSITIVOS POR MÓDULO)
// ======================================
const DISPOSITIVOS_OFICIALES = [
  "HP P22va G4 FHD Monitor",
  "HP P22va G4 FHD Monitor",
  "SWIFT PRO",
  "CPU HP G5",
  "Suprema Real Scan G-10",
  "Wacom STU-530",
  "Smart PinPad",
  "Brazo Articulado",
  "Rebel T7",
  "Microfono USB de condensador",
  "Xerox D35 SCANNER",
];

export default function Dashboard() {
  const [inventario, setInventario] = useState([]);

  // ================================
  // CARGAR INVENTARIO
  // ================================
  useEffect(() => {
    async function loadData() {
      try {
        const res = await axios.get(`${API_BASE}/inventario`);
        setInventario(res.data || []);
      } catch (error) {
        console.error("❌ Error cargando inventario:", error);
      }
    }
    loadData();
  }, []);

  // ================================
  // KPI’S Y CONTADORES
  // ================================
  const totalEquipos = inventario.length;

  const equiposPorMunicipio = inventario.reduce((acc, item) => {
    const key = item.municipio || "Sin municipio";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const equiposPorTipo = inventario.reduce((acc, item) => {
    const key = item.equipo || "Sin tipo";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const equiposEstado = inventario.reduce((acc, item) => {
    const key = item.estadoEquipo || "Sin estado";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const criticos = inventario.filter(
    (i) => (i.estadoEquipo || "").toUpperCase() !== "BUENO"
  );

  const saludGlobal =
    totalEquipos === 0
      ? 0
      : Math.round(((totalEquipos - criticos.length) / totalEquipos) * 100);

  const ultimosEquipos = [...inventario]
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    .slice(0, 5);

  // ==============================================
  // AGRUPAR OFICINA → MÓDULO
  // ==============================================
  const modulosPorOficina = inventario.reduce((acc, item) => {
    const oficina = item.oficina || "Sin oficina";
    const modulo = item.modulo || "Sin módulo";

    if (!acc[oficina]) acc[oficina] = {};
    if (!acc[oficina][modulo]) acc[oficina][modulo] = [];

    acc[oficina][modulo].push(item);
    return acc;
  }, {});

  // ==============================================
  // DIAGNÓSTICO COMPLETO DE MÓDULOS
  // ==============================================
  const diagnosticoModulos = [];

  Object.entries(modulosPorOficina).forEach(([oficina, modulos]) => {
    Object.entries(modulos).forEach(([modulo, equipos]) => {
      const nombresInventario = equipos.map((e) => e.equipo);

      const faltantes = DISPOSITIVOS_OFICIALES.filter(
        (req) => !nombresInventario.includes(req)
      );

      diagnosticoModulos.push({
        oficina,
        municipio: equipos[0]?.municipio || "N/D",
        modulo,
        totalActual: equipos.length,
        faltantes,
        faltanCantidad: faltantes.length,
        completo: faltantes.length === 0,
      });
    });
  });

  const modulosIncompletos = diagnosticoModulos.filter((m) => !m.completo);

  const totalModulos = diagnosticoModulos.length;
  const modulosCompletos = totalModulos - modulosIncompletos.length;

  const saludModulos =
    totalModulos === 0
      ? 0
      : Math.round((modulosCompletos / totalModulos) * 100);

  // ==============================================
  // FALLAS POR OFICINA
  // ==============================================
  const fallasPorOficina = criticos.reduce((acc, item) => {
    const key = item.oficina || "Sin oficina";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const topFallasOficina = Object.entries(fallasPorOficina).sort(
    (a, b) => b[1] - a[1]
  );

  // ==============================================
  // GRÁFICAS
  // ==============================================
  const donutData = {
    labels: Object.keys(equiposPorTipo),
    datasets: [
      {
        data: Object.values(equiposPorTipo),
        backgroundColor: [
          "#0ea5e9",
          "#9333ea",
          "#ef4444",
          "#22c55e",
          "#f59e0b",
          "#3b82f6",
          "#a855f7",
        ],
        borderWidth: 2,
      },
    ],
  };

  const barMunicipioData = {
    labels: Object.keys(equiposPorMunicipio),
    datasets: [
      {
        label: "Equipos por Municipio",
        data: Object.values(equiposPorMunicipio),
        backgroundColor: "rgba(14, 165, 233, 0.7)",
        borderColor: "#0ea5e9",
        borderWidth: 2,
        borderRadius: 5,
      },
    ],
  };

  const barEstadoData = {
    labels: Object.keys(equiposEstado),
    datasets: [
      {
        label: "Equipos por Estado",
        data: Object.values(equiposEstado),
        backgroundColor: "rgba(56, 189, 248, 0.7)",
        borderColor: "#06b6d4",
        borderWidth: 2,
        borderRadius: 5,
      },
    ],
  };

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <div style={{ padding: 40, maxWidth: "1500px", margin: "0 auto" }}>
      <h1
        style={{
          fontSize: "32px",
          fontWeight: "800",
          color: "#0f172a",
          textShadow: "0px 0px 10px rgba(59,130,246,0.35)",
        }}
      >
        Dashboard de Inventario
      </h1>

      {/* ========================== KPIs ============================= */}
      <div style={kpiGrid}>
        <KPI title="Equipos Totales" value={totalEquipos} icon="📦" />
        <KPI
          title="Municipios"
          value={Object.keys(equiposPorMunicipio).length}
          icon="🏙️"
        />
        <KPI
          title="Tipos de Equipo"
          value={Object.keys(equiposPorTipo).length}
          icon="🛠️"
        />
        <KPI
          title="Estados Registrados"
          value={Object.keys(equiposEstado).length}
          icon="⚙️"
        />
        <KPI title="Total de Módulos" value={totalModulos} icon="🧮" />
        <KPI
          title="Salud Global"
          value={`${saludGlobal}%`}
          icon="💡"
          highlight={saludGlobal < 100}
        />
        <KPI
          title="Salud de Módulos"
          value={`${saludModulos}%`}
          icon="🧩"
          highlight={saludModulos < 100}
        />
      </div>

      {/* ========================= GRÁFICAS ============================ */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "30px",
          marginTop: "40px",
        }}
      >
        <div>
          <h3 style={titleGraph}>Distribución por Tipo</h3>
          <Doughnut data={donutData} />
        </div>

        <div>
          <h3 style={titleGraph}>Equipos por Municipio</h3>
          <Bar data={barMunicipioData} />
        </div>
      </div>

      {/* ====================== MÓDULOS + FALLAS + ULTIMOS ====================== */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.2fr 1fr",
          gap: "30px",
          marginTop: "40px",
        }}
      >
        {/* ESTADOS */}
        <div>
          <h3 style={titleGraph}>Equipos por Estado</h3>
          <Bar data={barEstadoData} />

          {/* RESUMEN */}
          <div style={{ ...panelCard, marginTop: 20 }}>
            <h3 style={panelTitle}>Resumen por Estado</h3>

            {Object.entries(equiposEstado).map(([estado, total]) => {
              const porcentaje = Math.round((total / totalEquipos) * 100);
              return (
                <div key={estado} style={{ marginBottom: 15 }}>
                  <strong>
                    {estado} ({total})
                  </strong>
                  <div
                    style={{
                      width: "100%",
                      height: 8,
                      background: "#e5e7eb",
                      borderRadius: 4,
                      marginTop: 4,
                    }}
                  >
                    <div
                      style={{
                        width: `${porcentaje}%`,
                        height: "100%",
                        background: "#3b82f6",
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* PANEL DERECHO */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {/* ================== Equipos en Riesgo ================== */}
          <div style={panelCardWarning}>
            <h3 style={{ ...panelTitle, color: "#b91c1c" }}>
              Equipos en Riesgo
            </h3>

            {criticos.length === 0 ? (
              <p>No hay equipos en riesgo.</p>
            ) : (
              criticos.map((e, i) => (
                <div
                  key={i}
                  style={{
                    marginBottom: 12,
                    paddingBottom: 10,
                    borderBottom: "1px solid #fca5a5",
                  }}
                >
                  <p style={{ margin: 0, fontSize: 16 }}>
                    ⚠️ <strong>{e.equipo}</strong> — {e.estadoEquipo}
                  </p>

                  <p style={{ margin: "4px 0 0 28px", fontSize: 14 }}>
                    🏢 <strong>Oficina:</strong> {e.oficina ?? "N/D"} <br />
                    🧩 <strong>Módulo:</strong> {e.modulo ?? "N/D"}
                  </p>
                </div>
              ))
            )}
          </div>

          {/* ================== Fallas por Oficina ================== */}
          <div style={panelCard}>
            <h3 style={panelTitle}>Fallas por Oficina</h3>
            {topFallasOficina.map(([oficina, total]) => (
              <div
                key={oficina}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <span>{oficina}</span>
                <strong style={{ color: "#dc2626" }}>{total}</strong>
              </div>
            ))}
          </div>

          {/* ================== Últimos agregados ================== */}
          {ultimosEquipos.map((e, i) => {
            const fecha = e.createdAt
              ? new Date(e.createdAt).toLocaleDateString("es-MX", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "Fecha no disponible";

            return (
              <div key={i} style={{ marginBottom: 16 }}>
                <strong style={{ fontSize: 17 }}>{e.equipo}</strong>
                <br />
                <small style={{ color: "#475569" }}>
                  {e.oficina} — {e.municipio}
                </small>
                <br />
                <small style={{ color: "#0284c7", fontWeight: "600" }}>
                  📅 {fecha}
                </small>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ============================== ESTILOS ============================== */

const kpiGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 20,
};

function KPI({ title, value, icon, highlight }) {
  return (
    <div
      style={{
        background: "#111827",
        color: "white",
        padding: 20,
        borderRadius: 16,
        textAlign: "center",
        border: highlight ? "2px solid #fecaca" : "2px solid transparent",
      }}
    >
      <div style={{ fontSize: 28 }}>{icon}</div>
      <div style={{ fontSize: 14, marginTop: 6 }}>{title}</div>
      <div style={{ fontSize: 32, fontWeight: "bold" }}>{value}</div>
    </div>
  );
}

const panelCard = {
  background: "#fff",
  padding: 16,
  borderRadius: 12,
  border: "1px solid #e5e7eb",
};

const panelCardWarning = {
  background: "#fef2f2",
  padding: 16,
  borderRadius: 12,
  border: "1px solid #fecaca",
};

const panelTitle = {
  fontSize: 16,
  fontWeight: "700",
  marginBottom: 10,
};

const titleGraph = {
  fontSize: 18,
  marginBottom: 10,
  fontWeight: "600",
};
